import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import type {
  AdminUserDetail,
  AdminUserListItem,
  PlanFilter,
  UserStatus,
  UserStatusFlipTarget,
} from '@/users/schemas'

export const USERS_QUERY_KEY = ['admin', 'users'] as const
export const USER_DETAIL_QUERY_KEY = ['admin', 'user-detail'] as const

export interface UsersFilter {
  status?: UserStatus
  email?: string
  name?: string
  dapVerified?: boolean
  plan?: PlanFilter
  createdFrom?: string
  createdTo?: string
  /** Csak az elmúlt N napban aktív userek (DEC-ADMIN-015; 1/3/7/30). */
  activeWithinDays?: number
  /** Csak az aktív visszaélés-ban alatt álló userek (DEC-DAP-024). */
  abuseBanned?: boolean
  page?: number
}

export interface UsersPageMeta {
  current_page: number
  last_page: number
  total: number
}

export interface UsersPage {
  items: AdminUserListItem[]
  meta: UsersPageMeta
}

/**
 * `GET /admin/users` (DEC-ADMIN-006 / DEC-ADMIN-014).
 *
 * Paginált lista; szűrők: `status`, `email`, `name` (nick_name), `dap_verified`,
 * `plan` (free/premium), `created_from`/`created_to` + `page`. Default
 * per_page=25; sort `created_at DESC + id DESC`. A paginated envelope
 * `data`+`meta` mezőit manuálisan extracteljük.
 */
export function useUsers(filter: UsersFilter = {}) {
  return useQuery({
    queryKey: [...USERS_QUERY_KEY, filter] as const,
    queryFn: async (): Promise<UsersPage> => {
      const query: Record<string, string | number | boolean> = {}
      if (filter.status) query.status = filter.status
      if (filter.email) query.email = filter.email
      if (filter.name) query.name = filter.name
      if (filter.dapVerified !== undefined) query.dap_verified = filter.dapVerified
      if (filter.plan) query.plan = filter.plan
      if (filter.createdFrom) query.created_from = filter.createdFrom
      if (filter.createdTo) query.created_to = filter.createdTo
      if (filter.activeWithinDays) query.active_within_days = filter.activeWithinDays
      if (filter.abuseBanned) query.abuse_banned = true
      if (filter.page && filter.page > 1) query.page = filter.page

      const { data, error } = await apiClient.GET('/admin/users', {
        params: Object.keys(query).length > 0 ? { query } : undefined,
      })
      if (error) throw error
      const envelope = data as
        | { data?: AdminUserListItem[]; meta?: Partial<UsersPageMeta> }
        | undefined
      const items = envelope?.data ?? []
      return {
        items,
        meta: {
          current_page: envelope?.meta?.current_page ?? 1,
          last_page: envelope?.meta?.last_page ?? 1,
          total: envelope?.meta?.total ?? items.length,
        },
      }
    },
  })
}

/**
 * `GET /admin/users/{id}` — felhasználó-részletlap (DEC-ADMIN-014): fiók +
 * profil + DÁP/founder + billing-blokk.
 */
export function useUserDetail(id: string | null) {
  return useQuery({
    queryKey: [...USER_DETAIL_QUERY_KEY, id] as const,
    queryFn: async (): Promise<AdminUserDetail> => {
      const { data, error } = await apiClient.GET('/admin/users/{id}', {
        params: { path: { id: id ?? '' } },
      })
      if (error) throw error
      const envelope = data as { data?: AdminUserDetail } | undefined
      if (!envelope?.data) throw new Error('missing detail payload')
      return envelope.data
    },
    enabled: !!id,
  })
}

interface FlipUserStatusVariables {
  // DEC-SECURITY-002 §Tier 5 — opaque user public_id ULID.
  id: string
  status: UserStatusFlipTarget
  reason?: string | null
  /**
   * DEC-DAP-024 — visszaélés miatti letiltásnál (csak `disabled` + DÁP-hitelesített
   * user) a DÁP-lenyomat 3 évre ban-listára kerül (a törlés+újraregisztráció-
   * megkerülés ellen). Rendes letiltásnál hagyd falsy-n.
   */
  abuseBan?: boolean
}

/**
 * `PATCH /admin/users/{id}/status` (DEC-ADMIN-004).
 *
 * Status flip `active <-> disabled`-ig; a backend `moderation_actions`
 * row-t ír (audit trail). Letiltásnál a `reason` KÖTELEZŐ (DEC-ADMIN-014),
 * feloldásnál opcionális. Idempotens: azonos state no-op.
 */
export function useFlipUserStatus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      status,
      reason,
      abuseBan,
    }: FlipUserStatusVariables): Promise<void> => {
      const body: {
        status: UserStatusFlipTarget
        reason?: string | null
        abuse_ban?: boolean
      } = { status }
      if (reason != null) body.reason = reason
      if (abuseBan) body.abuse_ban = true
      const { error } = await apiClient.PATCH('/admin/users/{id}/status', {
        params: { path: { id } },
        body,
      })
      if (error) throw error
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: USER_DETAIL_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: ['admin', 'moderation-actions'] })
    },
  })
}

interface CompPremiumGrantVariables {
  id: string
  months: number
  reason: string
}

/**
 * `POST /admin/users/{id}/comp-premium` (DEC-ADMIN-014) — kompenzációs
 * prémium: aktív subnál +N hónap idő-tengely, különben új `admin_comp` sub.
 * Indok kötelező, moderation_actions-naplós.
 */
export function useGrantCompPremium() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, months, reason }: CompPremiumGrantVariables): Promise<void> => {
      const { error } = await apiClient.POST('/admin/users/{id}/comp-premium', {
        params: { path: { id } },
        body: { months, reason },
      })
      if (error) throw error
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: USER_DETAIL_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: ['admin', 'moderation-actions'] })
    },
  })
}

interface CompPremiumRevokeVariables {
  id: string
  reason: string
}

/**
 * `POST /admin/users/{id}/comp-premium/revoke` (DEC-ADMIN-014) — CSAK
 * nem-fizetett (admin_comp/founder_grant) subra; fizetettnél a backend 422
 * (nincs refund-flow).
 */
export function useRevokeCompPremium() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, reason }: CompPremiumRevokeVariables): Promise<void> => {
      const { error } = await apiClient.POST('/admin/users/{id}/comp-premium/revoke', {
        params: { path: { id } },
        body: { reason },
      })
      if (error) throw error
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: USER_DETAIL_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: ['admin', 'moderation-actions'] })
    },
  })
}

/**
 * DEC-ADMIN-015 — support/GDPR-akciók: a meglévő user-oldali GDPR-flow-kat
 * hívják admin-oldalról (érintetti kérelemre); az indok a naplóba kerül.
 */
function supportMutation(
  path:
    | '/admin/users/{id}/resend-verification'
    | '/admin/users/{id}/password-reset-email'
    | '/admin/users/{id}/gdpr/deletion-request'
    | '/admin/users/{id}/gdpr/deletion-cancel'
    | '/admin/users/{id}/gdpr/export',
) {
  return async ({ id, reason }: { id: string; reason?: string }): Promise<void> => {
    const { error } = await apiClient.POST(path, {
      params: { path: { id } },
      ...(reason !== undefined ? { body: { reason } } : {}),
    })
    if (error) throw error
  }
}

function useSupportAction(path: Parameters<typeof supportMutation>[0]) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: supportMutation(path),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: USER_DETAIL_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: ['admin', 'moderation-actions'] })
    },
  })
}

export function useResendVerification() {
  return useSupportAction('/admin/users/{id}/resend-verification')
}

export function useSendPasswordResetEmail() {
  return useSupportAction('/admin/users/{id}/password-reset-email')
}

export function useGdprDeletionRequest() {
  return useSupportAction('/admin/users/{id}/gdpr/deletion-request')
}

export function useGdprDeletionCancel() {
  return useSupportAction('/admin/users/{id}/gdpr/deletion-cancel')
}

export function useGdprExportRequest() {
  return useSupportAction('/admin/users/{id}/gdpr/export')
}

export interface FixBillingDataFields {
  billing_name?: string
  billing_post_code?: string
  billing_city?: string
  billing_address?: string
  billing_taxcode?: string | null
}

interface FixBillingDataVariables {
  // DEC-SECURITY-002 §Tier 5 — opaque checkout session public_id ULID.
  sessionId: string
  fields: FixBillingDataFields
}

/**
 * `PATCH /admin/checkout-sessions/{id}/billing-data` (DEC-ADMIN-014) — a
 * `billing:fix-billing-data` CLI felületi párja: a beküldött mezők
 * felülírnak (merge), a taxcode üres stringgel törölhető; a MERGE-ELT
 * végállapotot a backend validálja, majd a számla újra sorba áll.
 */
export function useFixBillingData() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ sessionId, fields }: FixBillingDataVariables): Promise<void> => {
      const { error } = await apiClient.PATCH('/admin/checkout-sessions/{id}/billing-data', {
        params: { path: { id: sessionId } },
        body: fields,
      })
      if (error) throw error
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: USER_DETAIL_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: ['admin', 'moderation-actions'] })
    },
  })
}
